import { useProfitAndLoss } from "@/contexts/ProfitAndLossAnalyticsContext";
// import { Section, Table } from "./components/Sections";
import styles from "./page.module.css";
import { useEffect } from "react";
import {Table, TableColumn} from "@/components/Table/Table";
import ComparisonRangeSelector from "./components/ComparisonRangeSelector/ComparisonRangeSelector";

const formatCurrency = (amount: number) => {
 return new Intl.NumberFormat('en-US', {
   style: 'currency',
   currency: 'USD',
   minimumFractionDigits: 2
 }).format(amount);
};

function formatDateRange(startTimestamp: number, endTimestamp: number) {
    if (!startTimestamp || !endTimestamp) {
        return '';
    }
    const start = new Date(startTimestamp);
    const end = new Date(endTimestamp);
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    // Calculate if dates are in different years or exactly one year apart
    const isYearOrMore = 
        (end.getTime() - start.getTime()) >= 365 * 24 * 60 * 60 * 1000;

    const startFormatted = isYearOrMore 
        ? `${months[start.getMonth()]} ${start.getDate()}, ${start.getFullYear()}`
        : `${months[start.getMonth()]} ${start.getDate()}`;

    return `${startFormatted} - ${months[end.getMonth()]} ${end.getDate()}, ${end.getFullYear()}`;
}

interface RowCell {
 content: string;
 className?: string;
}

interface Row {
 cells: RowCell[];
 className?: string;
}

interface RowOptions {
 labelClass?: string;
 valueClass?: string;
 rowClass?: string;
}

interface RowData {
    category: string;
    firstPeriod: number;
    secondPeriod: number;
    difference: number;
}

const ProfitAndLossPage = () => {
    const {
        profitAndLossData,
        isLoading,
        error,
        timeFrame,
        setTimeFrame,
        fetchProfitAndLossData,
        previousTimeFrame,
    } = useProfitAndLoss();

    useEffect(() => {
        fetchProfitAndLossData();
    }, [fetchProfitAndLossData]);

    // if (isLoading) return <div>Loading...</div>;
    // if (error) return <div>{error}</div>;
    // if (!profitAndLossData) return null;

    const columns: TableColumn<RowData>[] = [
        {
            title: 'Category',
            dataKey: 'category',
            id: 'category',
            width: '19rem',
            pinned: 'left',
            renderRowCell: (row) => (
                <div style={{
                    color: row.getCellData('category') === 'Total Revenue' ? 'var(--static-text-strong)' : 'inherit',
                    fontWeight: row.getCellData('category') === 'Total Revenue' ? 500 : 'normal'
                }}>
                    {row.getCellData('category')}
                </div>
            )
        },
        {
            title: formatDateRange(profitAndLossData?.firstPeriod.dateRange.from, profitAndLossData?.firstPeriod.dateRange.to),
            dataKey: 'firstPeriod',
            id: 'firstPeriod',
            width: '12rem',
            renderRowCell: (row) => (
                <div style={{
                    color: row.getCellData('category') === 'Total Revenue' ? 'var(--static-text-strong)' : 'inherit',
                    fontWeight: row.getCellData('category') === 'Total Revenue' ? 500 : 'normal'
                }}>
                    {formatCurrency(row.getCellData('firstPeriod'))}
                </div>
            )
        },
        {
            title: formatDateRange(profitAndLossData?.secondPeriod.dateRange.from, profitAndLossData?.secondPeriod.dateRange.to),
            dataKey: 'secondPeriod',
            id: 'secondPeriod',
            width: '12rem',
            renderRowCell: (row) => (
                <div style={{
                    color: row.getCellData('category') === 'Total Revenue' ? 'var(--static-text-strong)' : 'inherit',
                    fontWeight: row.getCellData('category') === 'Total Revenue' ? 500 : 'normal'
                }}>
                    {formatCurrency(row.getCellData('secondPeriod'))}
                </div>
            )
        },
        {
            title: 'Difference',
            dataKey: 'difference',
            id: 'difference',
            width: '12rem',
            renderRowCell: (row) => (
                <div style={{
                    color: row.getCellData('category') === 'Total Revenue' ? 'var(--static-text-strong)' : 'inherit',
                    fontWeight: row.getCellData('category') === 'Total Revenue' ? 500 : 'normal'
                }}>
                    {formatCurrency(row.getCellData('difference'))}
                </div>
            )
        }
    ];

    const summaryColumns: TableColumn<RowData>[] = [
        {
            title: 'Category',
            dataKey: 'category',
            id: 'category',
            width: '19rem',
            pinned: 'left',
            renderRowCell: (row) => (
                <div style={{
                    color: row.getCellData('category') === 'Total Revenue' ? 'var(--static-text-strong)' : 'inherit',
                    fontWeight: row.getCellData('category') === 'Total Revenue' ? 500 : 'normal'
                }}>
                    {row.getCellData('category')}
                </div>
            )
        },
        {
            title: formatDateRange(profitAndLossData?.firstPeriod.dateRange.from, profitAndLossData?.firstPeriod.dateRange.to),
            dataKey: 'firstPeriod',
            id: 'firstPeriod',
            width: '12rem',
            renderRowCell: (row) => (
                <div style={{
                    color: row.getCellData('category') === 'Total Revenue' ? 'var(--static-text-strong)' : 'inherit',
                    fontWeight: row.getCellData('category') === 'Total Revenue' ? 500 : 'normal'
                }}>
                    {row.getCellData('category') === 'Net Margin' 
                        ? `${formatCurrency(row.getCellData('firstPeriod'))}%`
                        : formatCurrency(row.getCellData('firstPeriod'))}
                </div>
            )
        },
        {
            title: formatDateRange(profitAndLossData?.secondPeriod.dateRange.from, profitAndLossData?.secondPeriod.dateRange.to),
            dataKey: 'secondPeriod',
            id: 'secondPeriod',
            width: '12rem',
            renderRowCell: (row) => (
                <div style={{
                    color: row.getCellData('category') === 'Total Revenue' ? 'var(--static-text-strong)' : 'inherit',
                    fontWeight: row.getCellData('category') === 'Total Revenue' ? 500 : 'normal'
                }}>
                    {row.getCellData('category') === 'Net Margin' 
                        ? `${formatCurrency(row.getCellData('secondPeriod'))}%`
                        : formatCurrency(row.getCellData('secondPeriod'))}
                </div>
            )
        },
        {
            title: 'Difference',
            dataKey: 'difference',
            id: 'difference',
            width: '12rem',
            renderRowCell: (row) => (
                <div style={{
                    color: row.getCellData('category') === 'Total Revenue' ? 'var(--static-text-strong)' : 'inherit',
                    fontWeight: row.getCellData('category') === 'Total Revenue' ? 500 : 'normal'
                }}>
                    {row.getCellData('category') === 'Net Margin' 
                        ? `${formatCurrency(row.getCellData('difference'))}%`
                        : formatCurrency(row.getCellData('difference'))}
                </div>
            )
        }
    ];

    const revenueData = profitAndLossData ? [
        {
          category: 'Amazon Sales',  
          firstPeriod: profitAndLossData.firstPeriod.revenue.amazonSales,
          secondPeriod: profitAndLossData.secondPeriod.revenue.amazonSales,
          difference: profitAndLossData.difference.revenue.amazonSales
        },
        {
          category: 'Refunds',
          firstPeriod: profitAndLossData.firstPeriod.revenue.refunds,
          secondPeriod: profitAndLossData.secondPeriod.revenue.refunds, 
          difference: profitAndLossData.difference.revenue.refunds
        },
        {
          category: 'Reimbursements',
          firstPeriod: profitAndLossData.firstPeriod.revenue.reimbursements,
          secondPeriod: profitAndLossData.secondPeriod.revenue.reimbursements,
          difference: profitAndLossData.difference.revenue.reimbursements
        },
        {
          category: 'Promo Rebates',
          firstPeriod: profitAndLossData.firstPeriod.revenue.promoRebates,
          secondPeriod: profitAndLossData.secondPeriod.revenue.promoRebates,
          difference: profitAndLossData.difference.revenue.promoRebates
        },
        {
          category: 'Other Revenue',
          firstPeriod: profitAndLossData.firstPeriod.revenue.otherRevenue,
          secondPeriod: profitAndLossData.secondPeriod.revenue.otherRevenue,
          difference: profitAndLossData.difference.revenue.otherRevenue
        },
        {
          category: 'Total Revenue',
          firstPeriod: profitAndLossData.firstPeriod.revenue.totalRevenue,
          secondPeriod: profitAndLossData.secondPeriod.revenue.totalRevenue,
          difference: profitAndLossData.difference.revenue.totalRevenue
        }
    ] : [];

    const costsData = profitAndLossData ? [
        {
            category: 'Unit Cost',
            firstPeriod: profitAndLossData.firstPeriod.costs.unitCost,
            secondPeriod: profitAndLossData.secondPeriod.costs.unitCost,
            difference: profitAndLossData.difference.costs.unitCost
        },
        {
            category: 'Supplier Shipping',
            firstPeriod: profitAndLossData.firstPeriod.costs.supplierShipping,
            secondPeriod: profitAndLossData.secondPeriod.costs.supplierShipping,
            difference: profitAndLossData.difference.costs.supplierShipping
        },
        {
            category: 'FBA Inbound Shipping',
            firstPeriod: profitAndLossData.firstPeriod.costs.fbaInboundShipping,
            secondPeriod: profitAndLossData.secondPeriod.costs.fbaInboundShipping,
            difference: profitAndLossData.difference.costs.fbaInboundShipping
        },
        {
            category: 'Order Fees',
            firstPeriod: profitAndLossData.firstPeriod.costs.orderFees,
            secondPeriod: profitAndLossData.secondPeriod.costs.orderFees,
            difference: profitAndLossData.difference.costs.orderFees
        },
        {
            category: 'Miscellaneous Cost',
            firstPeriod: profitAndLossData.firstPeriod.costs.miscellaneousCost,
            secondPeriod: profitAndLossData.secondPeriod.costs.miscellaneousCost,
            difference: profitAndLossData.difference.costs.miscellaneousCost
        },
        {
            category: 'Total Cost of Goods Sold',
            firstPeriod: profitAndLossData.firstPeriod.costs.totalCostOfGoodsSold,
            secondPeriod: profitAndLossData.secondPeriod.costs.totalCostOfGoodsSold,
            difference: profitAndLossData.difference.costs.totalCostOfGoodsSold
        },
        {
            category: 'Gross Profit',
            firstPeriod: profitAndLossData.firstPeriod.grossProfit,
            secondPeriod: profitAndLossData.secondPeriod.grossProfit,
            difference: profitAndLossData.difference.grossProfit
        },
        {
            category: 'Gross Margin',
            firstPeriod: profitAndLossData.firstPeriod.grossMargin,
            secondPeriod: profitAndLossData.secondPeriod.grossMargin,
            difference: profitAndLossData.difference.grossMargin
        }
    ] : [];

    const operatingExpensesData = profitAndLossData ? [
        {
            category: 'Seller Fees',
            firstPeriod: profitAndLossData.firstPeriod.operatingExpenses.sellerFees,
            secondPeriod: profitAndLossData.secondPeriod.operatingExpenses.sellerFees,
            difference: profitAndLossData.difference.operatingExpenses.sellerFees
        },
        {
            category: 'Sales Tax',
            firstPeriod: profitAndLossData.firstPeriod.operatingExpenses.salesTax,
            secondPeriod: profitAndLossData.secondPeriod.operatingExpenses.salesTax,
            difference: profitAndLossData.difference.operatingExpenses.salesTax
        },
        {
            category: 'Total Operating Expenses',
            firstPeriod: profitAndLossData.firstPeriod.operatingExpenses.sellerFees + profitAndLossData.firstPeriod.operatingExpenses.salesTax,
            secondPeriod: profitAndLossData.secondPeriod.operatingExpenses.sellerFees + profitAndLossData.secondPeriod.operatingExpenses.salesTax,
            difference: profitAndLossData.difference.operatingExpenses.sellerFees + profitAndLossData.difference.operatingExpenses.salesTax
        }
    ] : [];

    const summaryData = profitAndLossData ? [
        {
            category: 'Net Profit',
            firstPeriod: profitAndLossData.firstPeriod.netProfit,
            secondPeriod: profitAndLossData.secondPeriod.netProfit,
            difference: profitAndLossData.difference.netProfit
        },
        {
            category: 'Net Margin',
            firstPeriod: profitAndLossData.firstPeriod.netMargin,
            secondPeriod: profitAndLossData.secondPeriod.netMargin,
            difference: profitAndLossData.difference.netMargin
        }
    ]: [];


    return (
        <div className={styles.container}>
            <div className={styles.datePickerContainer}>
                <span className={styles.heading}>
                    Profit & Loss
                </span>
                <ComparisonRangeSelector
                    currentRange={timeFrame}
                    comparisonRange={previousTimeFrame}
                    onCurrentRangeChange={setTimeFrame}
                />
            </div>
            <div className={styles.tableContainer}>
                <div>
                    <div className={styles.title}>Revenue</div>
                    <Table
                        data={revenueData ?? []}
                        columns={columns}
                        tableName="revenue"
                        isLoading={isLoading}
                        columnControls={false}
                        className={styles.tableWrapper}
                        containerClassName={styles.tableOverwrite}
                    />
                </div>
                <div>
                    <div className={styles.title}>Costs of Goods Sold</div>
                    <Table
                        data={costsData}
                        columns={columns}
                        tableName="costs"
                        isLoading={isLoading}
                        columnControls={false}
                        className={styles.tableWrapper}
                        containerClassName={styles.tableOverwrite}
                    />
                </div>
                <div>
                    <div className={styles.title}>Costs of Goods Sold</div>
                    <Table
                        data={operatingExpensesData}
                        columns={columns}
                        tableName="operating-expenses"
                        isLoading={isLoading}
                        columnControls={false}
                        className={styles.tableWrapper}
                        containerClassName={styles.tableOverwrite}
                    />
                </div>
                <Table
                    data={summaryData}
                    columns={summaryColumns}
                    tableName="summary"
                    isLoading={isLoading}
                    columnControls={false}
                    containerClassName={styles.tableOverwrite}
                    showHeader={false}
                />
            </div>
        </div>
    );
};

export default ProfitAndLossPage